/**
 * Pro tracking-workspace selection hook.
 *
 * Loads eligible workspaces via react-query, remembers a per-user selection in
 * localStorage under `proTrackingWorkspace:<userId>`, and resolves the active
 * destination with `resolveTrackingWorkspace`. Does not touch citizenWorkspaceId.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getStoredUser } from '@shared/auth/session';
import api from '../../lib/api';
import {
  EligibleWorkspace,
  ResolveTrackingWorkspaceResult,
  resolveTrackingWorkspace,
} from './resolveTrackingWorkspace';

const STORAGE_PREFIX = 'proTrackingWorkspace:';

type RawWorkspace = {
  id?: string;
  name?: string;
  organization_name?: string;
  organizationName?: string;
  access_level?: string;
  accessLevel?: string;
};

function storageKeyForUser(userId: string | null | undefined): string | null {
  if (!userId) return null;
  return `${STORAGE_PREFIX}${userId}`;
}

function readUserId(): string | null {
  const user = getStoredUser<{ id?: string }>();
  const id = user?.id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

function readRememberedId(userId: string | null): string | null {
  const key = storageKeyForUser(userId);
  if (!key) return null;
  return localStorage.getItem(key);
}

function writeRememberedId(userId: string | null, workspaceId: string): void {
  const key = storageKeyForUser(userId);
  if (!key) return;
  localStorage.setItem(key, workspaceId);
}

function clearRememberedId(userId: string | null): void {
  const key = storageKeyForUser(userId);
  if (!key) return;
  localStorage.removeItem(key);
}

function normalizeEligible(raw: unknown): EligibleWorkspace[] {
  const rows: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)
      ? ((raw as { data: unknown[] }).data)
      : [];

  return rows
    .map((row): EligibleWorkspace | null => {
      if (!row || typeof row !== 'object') return null;
      const r = row as RawWorkspace;
      if (typeof r.id !== 'string' || !r.id) return null;
      const organization_name = r.organization_name ?? r.organizationName;
      const access_level = r.access_level ?? r.accessLevel;
      return {
        id: r.id,
        name: typeof r.name === 'string' ? r.name : r.id,
        ...(organization_name !== undefined ? { organization_name } : {}),
        ...(access_level !== undefined ? { access_level } : {}),
      };
    })
    .filter((w): w is EligibleWorkspace => w !== null);
}

export type UseProTrackingWorkspaceResult = ResolveTrackingWorkspaceResult & {
  eligible: EligibleWorkspace[];
  select: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
};

export function useProTrackingWorkspace(
  contextId?: string | null,
): UseProTrackingWorkspaceResult {
  const userId = readUserId();

  const [rememberedId, setRememberedId] = useState<string | null>(() =>
    readRememberedId(userId),
  );

  // Re-read when the signed-in user identity changes (different storage key).
  useEffect(() => {
    setRememberedId(readRememberedId(userId));
  }, [userId]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['pro-tracking-workspaces'],
    async () => {
      const response = await api.get('/workspace-bill-tracking/workspaces');
      return normalizeEligible(response.data?.data ?? response.data);
    },
    { staleTime: 30000 },
  );

  const eligible = useMemo(() => data ?? [], [data]);

  // Discard a stale remembered id from localStorage before consumers use it.
  useEffect(() => {
    if (isLoading || !data) return;
    if (!rememberedId) return;
    const stillEligible = eligible.some((w) => w.id === rememberedId);
    if (stillEligible) return;
    clearRememberedId(userId);
    setRememberedId(null);
  }, [isLoading, data, eligible, rememberedId, userId]);

  const resolved = useMemo(
    () =>
      resolveTrackingWorkspace({
        eligible,
        rememberedId,
        contextId,
      }),
    [eligible, rememberedId, contextId],
  );

  const select = useCallback(
    (id: string) => {
      writeRememberedId(userId, id);
      setRememberedId(id);
    },
    [userId],
  );

  return {
    ...resolved,
    eligible,
    select,
    isLoading,
    isError,
    error,
    refetch,
  };
}

export default useProTrackingWorkspace;
